<template>
  <div class="post-editor">
    <header class="editor-header">
      <div class="editor-nav">
        <div class="header-left">
          <router-link to="/admin/dashboard" class="btn btn-secondary">
            <i class="fas fa-arrow-left"></i>
            <span class="btn-text">Back to Dashboard</span>
          </router-link>
          <h1>{{ isEditing ? 'Edit Post' : 'Create New Post' }}</h1>
        </div>
        <div class="header-actions">
          <button 
            @click="saveAsDraft" 
            :disabled="saving || !title.trim()"
            class="btn btn-secondary"
          >
            <i class="fas fa-save"></i>
            <span class="btn-text">{{ saving ? 'Saving...' : 'Save Draft' }}</span>
          </button>
          <button 
            @click="saveAndPublish" 
            :disabled="saving || !title.trim() || !content.trim()"
            class="btn btn-primary"
          >
            <i class="fas fa-globe"></i>
            <span class="btn-text">{{ saving ? 'Publishing...' : 'Save & Publish' }}</span>
          </button>
        </div>
      </div>
    </header>

    <div v-if="error" class="error-message">
      {{ error }}
      <button @click="error = ''" class="btn-close">&times;</button>
    </div>

    <div v-if="success" class="success-message">
      {{ success }}
      <button @click="success = ''" class="btn-close">&times;</button>
    </div>

    <main class="editor-content">
      <div class="editor-form">
        <div class="form-row">
          <div class="form-group">
            <label for="title">Title *</label>
            <input
              type="text"
              id="title"
              v-model="title"
              @input="updateSlug"
              placeholder="Enter post title"
              :disabled="saving"
              required
            >
          </div>
          <div class="form-group">
            <label for="slug">Slug *</label>
            <input
              type="text"
              id="slug"
              v-model="slug"
              placeholder="post-url-slug"
              :disabled="saving"
              required
            >
          </div>
        </div>

        <div class="form-group">
          <label for="excerpt">Excerpt</label>
          <textarea
            id="excerpt"
            v-model="excerpt"
            placeholder="Brief description of the post (optional)"
            rows="3"
            :disabled="saving"
          ></textarea>
        </div>

        <div class="form-group">
          <label for="tags">Tags</label>
          <input
            type="text"
            id="tags"
            v-model="tagsInput"
            placeholder="Enter tags separated by commas"
            :disabled="saving"
          >
          <small class="form-help">Separate tags with commas (e.g., javascript, vue, web development)</small>
        </div>

        <div class="form-group">
          <label for="content">Content *</label>
          <div ref="editorContainer" class="editor-container"></div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useBlog } from '@/services/blog';
import EasyMDE from 'easymde';
import 'easymde/dist/easymde.min.css';

export default {
  name: 'PostEditor',
  props: {
    id: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const { getPost, createPost, updatePost } = useBlog();
    
    // Form data
    const title = ref('');
    const slug = ref('');
    const content = ref('');
    const excerpt = ref('');
    const tagsInput = ref('');
    const published = ref(false);
    
    // State
    const loading = ref(false);
    const saving = ref(false);
    const error = ref('');
    const success = ref('');
    const editor = ref(null);
    const editorContainer = ref(null);
    
    // Computed
    const isEditing = computed(() => !!props.id || !!route.params.id);
    const postId = computed(() => props.id || route.params.id);
    
    // Helper functions
    const generateSlug = (text) => {
      return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
    };
    
    const updateSlug = () => {
      if (!isEditing.value) {
        slug.value = generateSlug(title.value);
      }
    };
    
    const parseTags = (tagsString) => {
      if (!tagsString) return [];
      return tagsString
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
    };
    
    const formatTags = (tagsArray) => {
      if (!tagsArray || tagsArray.length === 0) return '';
      return tagsArray.join(', ');
    };
    
    // Editor setup
    const initializeEditor = () => {
      if (editor.value) {
        editor.value.toTextArea();
        editor.value = null;
      }
      
      if (editorContainer.value) {
        const textarea = document.createElement('textarea');
        textarea.value = content.value;
        editorContainer.value.appendChild(textarea);
        
        editor.value = new EasyMDE({
          element: textarea,
          placeholder: 'Write your post content in Markdown...',
          spellChecker: false,
          autofocus: false,
          toolbar: [
            'bold', 'italic', 'heading', '|',
            'quote', 'unordered-list', 'ordered-list', '|',
            'link', 'image', '|',
            'code', 'table', '|',
            'preview', 'side-by-side', 'fullscreen', '|',
            'guide'
          ],
          shortcuts: {
            togglePreview: 'Cmd-P',
            toggleSideBySide: null,
            toggleFullScreen: 'F11'
          },
          status: ['autosave', 'lines', 'words', 'cursor'],
          tabSize: 2,
        });
        
        editor.value.codemirror.on('change', () => {
          content.value = editor.value.value();
        });
      }
    };
    
    // Data loading
    const loadPost = async () => {
      if (!isEditing.value) return;
      
      loading.value = true;
      error.value = '';
      
      try {
        const post = await getPost(postId.value);
        title.value = post.title;
        slug.value = post.slug;
        content.value = post.content || '';
        excerpt.value = post.excerpt || '';
        tagsInput.value = formatTags(post.tags);
        published.value = post.published;
        
        // Update editor content
        if (editor.value) {
          editor.value.value(content.value);
        }
      } catch (err) {
        error.value = 'Failed to load post: ' + err.message;
      } finally {
        loading.value = false;
      }
    };
    
    // Save functions
    const savePost = async (shouldPublish = false) => {
      if (!title.value.trim()) {
        error.value = 'Title is required';
        return;
      }
      
      if (!slug.value.trim()) {
        error.value = 'Slug is required';
        return;
      }
      
      saving.value = true;
      error.value = '';
      success.value = '';
      
      try {
        const postData = {
          title: title.value.trim(),
          slug: slug.value.trim(),
          content: content.value,
          excerpt: excerpt.value.trim() || null,
          tags: parseTags(tagsInput.value),
          published: shouldPublish
        };
        
        let result;
        if (isEditing.value) {
          result = await updatePost(postId.value, postData);
        } else {
          result = await createPost(postData);
        }
        
        success.value = shouldPublish 
          ? 'Post saved and published successfully!'
          : 'Post saved as draft successfully!';
        
        // If creating new post, redirect to edit mode
        if (!isEditing.value && result.id) {
          setTimeout(() => {
            router.push({ name: 'EditPost', params: { id: result.id } });
          }, 1500);
        }
        
      } catch (err) {
        error.value = 'Failed to save post: ' + err.message;
      } finally {
        saving.value = false;
      }
    };
    
    const saveAsDraft = () => savePost(false);
    const saveAndPublish = () => savePost(true);
    
    // Lifecycle
    onMounted(async () => {
      await loadPost();
      initializeEditor();
    });
    
    onUnmounted(() => {
      if (editor.value) {
        editor.value.toTextArea();
        editor.value = null;
      }
    });
    
    // Watch for route changes
    watch(() => route.params.id, () => {
      loadPost();
    });
    
    return {
      title,
      slug,
      content,
      excerpt,
      tagsInput,
      loading,
      saving,
      error,
      success,
      isEditing,
      editorContainer,
      updateSlug,
      saveAsDraft,
      saveAndPublish,
    };
  },
};
</script>

<style scoped>
.post-editor {
  min-height: 100vh;
  background: #f8f9fa;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1001;
  overflow-y: auto;
}

.editor-header {
  background: linear-gradient(135deg, #2c3e50, #34495e);
  color: #fff;
  padding: 0;
  border-bottom: 1px solid #e1e5e9;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1002;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  height: 4.5rem;
  display: flex;
  align-items: center;
}

.editor-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-left h1 {
  margin: 0;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.error-message, .success-message {
  position: fixed;
  top: 5rem;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 600px;
  margin: 0;
  padding: 1rem;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1003;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.error-message {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.success-message {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  margin-left: 1rem;
  opacity: 0.6;
}

.btn-close:hover {
  opacity: 1;
}

.editor-content {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 4.5rem;
}

.editor-form {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  max-width: 800px;
  margin: 0 auto;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 0;
  border-bottom: 1px solid #e1e5e9;
}

.form-row .form-group {
  border-bottom: none;
  border-right: 1px solid #e1e5e9;
}

.form-row .form-group:last-child {
  border-right: none;
}

.form-group {
  padding: 1.5rem;
  border-bottom: 1px solid #e1e5e9;
}

.form-group:last-child {
  border-bottom: none;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #fafafa;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
  background: white;
  transform: translateY(-1px);
}

.form-group input:disabled,
.form-group textarea:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
  opacity: 0.7;
}

.form-help {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #666;
}

.editor-container {
  min-height: 400px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
  transition: all 0.3s ease;
}

.editor-container:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
  background: white;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .editor-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .header-actions {
    justify-content: center;
  }

  .editor-content {
    padding: 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-group {
    padding: 1rem;
  }
}
</style>

<style>
/* Global styles for EasyMDE */
.editor-container .EasyMDEContainer {
  background: white;
  border: none;
  border-radius: 8px;
  overflow: hidden;
}

.editor-container .EasyMDEContainer .editor-toolbar {
  border: none;
  border-bottom: 1px solid #e1e5e9;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.editor-container .EasyMDEContainer .CodeMirror {
  border: none;
  font-size: 15px;
  line-height: 1.7;
  min-height: 300px;
  background: white;
}

.editor-container .EasyMDEContainer .CodeMirror-focused {
  border: none;
  box-shadow: none;
}

.editor-container .EasyMDEContainer .editor-preview,
.editor-container .EasyMDEContainer .editor-preview-side {
  background: white;
  padding: 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  line-height: 1.6;
}

.editor-container .EasyMDEContainer .editor-preview h1,
.editor-container .EasyMDEContainer .editor-preview-side h1 {
  border-bottom: 1px solid #e1e5e9;
  padding-bottom: 0.5rem;
}

.editor-container .EasyMDEContainer .editor-preview code,
.editor-container .EasyMDEContainer .editor-preview-side code {
  background: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.9em;
}

.editor-container .EasyMDEContainer .editor-preview pre,
.editor-container .EasyMDEContainer .editor-preview-side pre {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
}

.editor-container .EasyMDEContainer .editor-preview blockquote,
.editor-container .EasyMDEContainer .editor-preview-side blockquote {
  border-left: 4px solid #007bff;
  padding-left: 1rem;
  margin-left: 0;
  color: #666;
}

@media (max-width: 768px) {
  .editor-header {
    height: auto;
    min-height: 3.8rem;
  }

  .editor-nav {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1rem;
  }

  .header-left {
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
  }

  .header-left h1 {
    font-size: 1.25rem;
    text-align: center;
  }

  .header-actions {
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .btn-text {
    display: none;
  }

  .btn {
    padding: 0.75rem;
    min-width: 44px;
    justify-content: center;
  }

  .editor-content {
    padding: 1rem;
    margin-top: 4rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-row .form-group {
    border-right: none;
    border-bottom: 1px solid #e1e5e9;
  }

  .form-row .form-group:last-child {
    border-bottom: none;
  }

  .form-group textarea {
    min-height: 200px;
  }
}

@media (max-width: 480px) {
  .editor-header {
    min-height: 3.5rem;
  }

  .editor-content {
    margin-top: 3.5rem;
    padding: 0.5rem;
  }

  .header-actions {
    gap: 0.25rem;
  }

  .btn {
    padding: 0.5rem;
    font-size: 0.875rem;
  }
}
</style>